import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteCertificateHolder } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { CertificateHolder } from "@shared/schema";

// CSR-745 T8: confirmation dialog for the per-row Delete action.
// Delete is hard-delete (no `deletedAt` on `certificate_holders`).
// Historical issuances retain their snapshot via
// `coi_issuances.certificateHolderName` -- but the holder vanishes
// from the picker and the list. The copy below mirrors that
// guarantee.

interface DeleteCertificateHolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  holder: CertificateHolder;
  onDeleted?: () => void;
}

export function DeleteCertificateHolderDialog({
  open,
  onOpenChange,
  holder,
  onDeleted,
}: DeleteCertificateHolderDialogProps) {
  const { toast } = useToast();
  const deleteMutation = useDeleteCertificateHolder();

  const handleConfirm = () => {
    deleteMutation.mutate(holder.id, {
      onSuccess: () => {
        toast({ title: "Certificate holder deleted" });
        onDeleted?.();
        onOpenChange(false);
      },
      onError: () => {
        toast({
          title: "Failed to delete certificate holder",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="dialog-delete-certificate-holder">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this certificate holder?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-semibold">{holder.name}</span> will be removed from the
            address book and the COI picker. Existing COI history retains its snapshot, but
            this holder will no longer appear in the list. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={deleteMutation.isPending}
            data-testid="button-confirm-delete-holder"
          >
            {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
